import React, { useRef, useState } from 'react'
import {
  StyleProp,
  TouchableOpacity,
  TouchableWithoutFeedback,
  View,
  ViewStyle,
  useWindowDimensions,
} from 'react-native'
import Animated, {
  runOnJS,
  useAnimatedKeyboard,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated'
import { Gesture, GestureDetector, ScrollView } from 'react-native-gesture-handler'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { Tooltip } from '@ui-kitten/components'
import { useKeyboardVisibility } from '@utils/hooks'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Button, ButtonProps, Text } from '@components/base'
import { ScrollViewWithFade } from '@components/ScrollViewWithFade'
import { useGoBack } from '@utils/navigation'
import Device from '@config/device'

export interface ActionButtonProps
  extends Omit<ButtonProps, 'accessibilityLabel' | 'children' | 'type' | 'size'> {
  type?: ButtonProps['type']
  text: string
  tooltipText?: string
}

interface BottomSheetProps {
  children: React.ReactNode
  dismissOnBackdropPress?: boolean
  header?: string | React.ReactNode
  primaryButton?: ActionButtonProps
  secondaryButton?: ActionButtonProps
  buttonDirection?: 'row' | 'column' | 'row-reverse' | 'column-reverse'
  showDismissButton?: boolean
  dismissButtonPosition?: 'top' | 'bottom'
  onDismiss?: () => void | Promise<void>

  // Fired once when the sheet is dismissed via backdrop, swipe, or the dismiss button.
  // Use for "mark seen" side effects.
  onClose?: () => void
  dismissButtonText?: string

  // Style applied to the sheet card container.
  style?: StyleProp<ViewStyle>

  // Style applied to the sheet card background/surface.
  backgroundStyle?: StyleProp<ViewStyle>

  // Optional fixed height as a single percentage entry, e.g. ['90%']. Kept for API
  // compatibility with the few callers that pinned a tall sheet; only the first
  // percentage value is honored and it is treated as a minimum content height.
  snapPoints?: (string | number)[]

  // Accepted for API compatibility with callers that selected an initial snap point; the sheet
  // always presents fully expanded, so this is ignored.
  index?: number
}

// Dismiss when a slow downward drag passes this fraction of the card height
// (clamped to the  min/max below) distance.
// A fast downward flick dismisses regardless of distance (see FLICK_VELOCITY).
const DISMISS_DISTANCE_RATIO = 0.3
const DISMISS_DISTANCE_MIN = 100
const DISMISS_DISTANCE_MAX = 220

// Downward fling velocity (px/s) that dismisses on a flick even when the drag was short.
const FLICK_VELOCITY = 1000

// Handle block height used in the content-height math (paddingVertical 8 * 2 + indicator 4).
const HANDLE_SIZE = 20

// In-house bottom sheet
//
// Live in transparent-modal navigation routes whose card transition provides the slide-in/out.
// This implementation is a bottom-anchored card: entrance/exit comes from the route transition,
// drag-to-dismiss from a pan gesture spanning the whole card (scroll-gated), and keyboard
// avoidance by extending the card background down behind the keyboard.
//
export const BottomSheet = ({
  children,
  dismissOnBackdropPress = true,
  header,
  primaryButton,
  secondaryButton,
  buttonDirection = 'row',
  showDismissButton = false,
  dismissButtonPosition = 'bottom',
  onDismiss,
  onClose,
  dismissButtonText = 'Dismiss',
  style,
  backgroundStyle,
  snapPoints,
}: BottomSheetProps) => {
  const styles = useStyleSheet(themedStyle)
  const goBack = useGoBack()
  const insets = useSafeAreaInsets()
  const dimensions = useWindowDimensions()

  const [headerHeight, setHeaderHeight] = useState(0)
  const [buttonsHeight, setButtonsHeight] = useState(0)
  const [primaryButtonTooltipVisible, setPrimaryButtonTooltipVisible] = useState(false)
  const [secondaryButtonTooltipVisible, setSecondaryButtonTooltipVisible] = useState(false)
  const [isDismissing, setIsDismissing] = useState(false)

  const translateY = useSharedValue(0)
  // Vertical scroll offset of the inner content, mirrored from onScroll so the pan worklet can
  // tell when the content is at the top (the only state in which dragging the card should move
  // the whole sheet rather than scroll its content).
  const scrollOffset = useSharedValue(0)
  const cardHeight = useSharedValue(0)
  const scrollRef = useRef<ScrollView>(null)
  const screenHeight = dimensions.height

  const handleDismiss = async () => {
    if (isDismissing) {
      return
    }

    setIsDismissing(true)

    try {
      onClose?.()
      await onDismiss?.()
    } finally {
      goBack()
    }
  }

  const handleBackdropPress = () => {
    if (dismissOnBackdropPress) {
      void handleDismiss()
    }
  }

  // Pan covers the whole card  but runs simultaneously with the
  // inner ScrollView and only translates the sheet while the content is scrolled to the top, so
  // mid-scroll drags/flicks scroll the content instead of dismissing. `activeOffsetY(10)` keeps
  // taps on cards/buttons working.
  //
  const panGesture = Gesture.Pan()
    .activeOffsetY(10)
    .simultaneousWithExternalGesture(scrollRef)
    .onUpdate((event) => {
      translateY.value = scrollOffset.value <= 0 && event.translationY > 0 ? event.translationY : 0
    })
    .onEnd((event) => {
      const atTop = scrollOffset.value <= 0
      const distanceThreshold = Math.min(
        Math.max((cardHeight.value || screenHeight) * DISMISS_DISTANCE_RATIO, DISMISS_DISTANCE_MIN),
        DISMISS_DISTANCE_MAX,
      )
      const shouldDismiss =
        atTop && (translateY.value > distanceThreshold || event.velocityY > FLICK_VELOCITY)

      if (shouldDismiss) {
        translateY.value = withTiming(screenHeight, { duration: 200 }, () => {
          runOnJS(handleDismiss)()
        })
      } else {
        // Settle back with no overshoot/bounce.
        translateY.value = withTiming(0, { duration: 180 })
      }
    })

  // Extend the card's own background down behind the keyboard (instead of a KeyboardAvoidingView
  // that pushes the whole card up and leaves the dark route overlay showing around the keyboard).
  // The padding lifts the content above the keyboard while the card's own background fills the
  // space behind it, so the overlay never shows around the keyboard. iOS-only: Android keyboard
  // handling is left to the OS (the previous KeyboardAvoidingView was also a no-op there).
  const keyboard = useAnimatedKeyboard()
  const isIOS = Device.ios
  const animatedCardStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: translateY.value }],
    paddingBottom: isIOS ? keyboard.height.value : 0,
  }))

  const renderHeader = () => {
    if (!header) {
      return null
    }

    if (typeof header === 'string') {
      return (
        <Text type="title-3" style={styles.title}>
          {header}
        </Text>
      )
    }

    return header
  }

  const hasAnyButton = !!(primaryButton || secondaryButton)
  const hasBothButtons = !!(primaryButton && secondaryButton)

  // In a row, buttons share the width via flex:1. In a column they must keep their intrinsic
  // height — flex:1 in an auto-height column collapses each button to ~0px (Yoga: flex-basis 0
  // with no free space to grow into). Column buttons stretch to full width by default instead.
  const isRowLayout = buttonDirection === 'row' || buttonDirection === 'row-reverse'

  const renderButton = ({
    button,
    defaultType,
    tooltipVisible,
    setTooltipVisible,
  }: {
    button: ActionButtonProps
    defaultType: ButtonProps['type']
    tooltipVisible: boolean
    setTooltipVisible: (visible: boolean) => void
  }) => {
    const buttonElement = (
      <Button
        accessibilityLabel={button.text}
        type={defaultType}
        size="block"
        style={[isRowLayout && styles.rowButton, button.style]}
        {...button}
      >
        {button.text}
      </Button>
    )

    if (!button.tooltipText) {
      return buttonElement
    }

    return (
      <Tooltip
        placement="top"
        visible={tooltipVisible}
        onBackdropPress={() => setTooltipVisible(false)}
        style={styles.tooltipContainer}
        anchor={() => (
          <TouchableOpacity
            activeOpacity={1}
            accessibilityLabel={button.text}
            onPress={() => setTooltipVisible(true)}
          >
            {buttonElement}
          </TouchableOpacity>
        )}
      >
        <Text type="small" style={styles.tooltipText}>
          {button.tooltipText}
        </Text>
      </Tooltip>
    )
  }

  const isKeyboardVisible = useKeyboardVisibility(true)
  const paddingBottom = isKeyboardVisible ? 16 : Math.max(insets.bottom, 16)

  const maxSheetHeight = dimensions.height - 80
  const maxContentHeight =
    maxSheetHeight - headerHeight - buttonsHeight - paddingBottom - HANDLE_SIZE

  // Honor a single percentage snap point (e.g. ['90%']) as a minimum content height so callers
  // that pinned a tall sheet keep that size; it is still clamped by maxContentHeight.
  const snapValue = snapPoints?.[0]
  const snapPercent =
    typeof snapValue === 'string' && snapValue.endsWith('%') ? parseFloat(snapValue) : NaN
  const minContentHeight = Number.isNaN(snapPercent)
    ? undefined
    : Math.max(
        0,
        (snapPercent / 100) * dimensions.height -
          headerHeight -
          buttonsHeight -
          paddingBottom -
          HANDLE_SIZE,
      )

  return (
    <View style={styles.container}>
      <TouchableWithoutFeedback accessibilityLabel="Backdrop" onPress={handleBackdropPress}>
        <View style={styles.backdrop} />
      </TouchableWithoutFeedback>
      <GestureDetector gesture={panGesture}>
        <Animated.View
          style={[styles.background, backgroundStyle, style, animatedCardStyle]}
          onLayout={(e) => {
            cardHeight.value = e.nativeEvent.layout.height
          }}
        >
          <View accessibilityLabel="Drag handle" style={styles.handle}>
            <View style={styles.handleIndicator} />
          </View>
          <View style={{ paddingBottom }}>
            <View
              style={styles.horizontalPadding}
              onLayout={(e) => setHeaderHeight(e.nativeEvent.layout.height)}
            >
              {showDismissButton && dismissButtonPosition === 'top' && (
                <View style={styles.dismissButtonContainer}>
                  <TouchableOpacity
                    onPress={() => void handleDismiss()}
                    accessibilityLabel="Dismiss"
                    hitSlop={{ top: 16, bottom: 16, left: 16, right: 16 }}
                  >
                    <Text type="regular" bold>
                      {dismissButtonText}
                    </Text>
                  </TouchableOpacity>
                </View>
              )}
              {renderHeader()}
            </View>
            <ScrollViewWithFade
              ref={scrollRef}
              onScroll={(e) => {
                scrollOffset.value = e.nativeEvent.contentOffset.y
              }}
              style={[
                styles.scrollView,
                { maxHeight: maxContentHeight, minHeight: minContentHeight },
              ]}
              scrollViewStyle={styles.horizontalPadding}
            >
              {children}
            </ScrollViewWithFade>
            <View
              style={[styles.horizontalPadding, { gap: 8 }]}
              onLayout={(e) => setButtonsHeight(e.nativeEvent.layout.height)}
            >
              <View style={{ flexDirection: buttonDirection, paddingTop: hasAnyButton ? 16 : 0 }}>
                {secondaryButton &&
                  renderButton({
                    button: secondaryButton,
                    defaultType: 'outline',
                    tooltipVisible: secondaryButtonTooltipVisible,
                    setTooltipVisible: setSecondaryButtonTooltipVisible,
                  })}
                {hasBothButtons && <View style={styles.buttonSpacer} />}
                {primaryButton &&
                  renderButton({
                    button: primaryButton,
                    defaultType: 'primary',
                    tooltipVisible: primaryButtonTooltipVisible,
                    setTooltipVisible: setPrimaryButtonTooltipVisible,
                  })}
              </View>
              {showDismissButton && dismissButtonPosition === 'bottom' && (
                <Button
                  type="transparent"
                  size="block"
                  onPress={() => void handleDismiss()}
                  accessibilityLabel="Dismiss"
                >
                  {dismissButtonText}
                </Button>
              )}
            </View>
          </View>
        </Animated.View>
      </GestureDetector>
    </View>
  )
}

const themedStyle = StyleService.create({
  container: {
    flexGrow: 1,
  },
  backdrop: {
    flexGrow: 1,
  },
  background: {
    backgroundColor: 'theme.background.modal',
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
  },
  handle: {
    paddingVertical: 8,
    alignItems: 'center',
  },
  handleIndicator: {
    width: 48,
    height: 4,
    borderRadius: 2,
    backgroundColor: 'theme.surface.base',
  },
  horizontalPadding: {
    paddingHorizontal: 16,
  },
  scrollView: {
    // Size the scroll area to its content, capped by maxHeight (then it scrolls).
    // Needs BOTH declarations to render on native AND web:
    //  - `flex: 0` shares the `flex` key, so StyleSheet.flatten replaces
    //    ScrollViewWithFade's own `flex: 1` here. On native that's essential: leaving
    //    `flex: 1` in place makes Yoga resolve flex-basis to 0 and the body collapses;
    //    with `flex: 0` Yoga resolves basis to auto (content-sized).
    //  - `flexBasis: 'auto'` is only for web (react-native-web): there `flex: 0`
    //    compiles to the CSS shorthand `flex: 0` i.e. `flex-basis: 0%`, which collapses
    //    the container when the content has no text-based min-content floor (the glucose
    //    gauge / macro sliders). RNW orders this longhand after the `flex` shorthand so
    //    it wins and restores basis auto. No-op on native.
    flex: 0,
    flexBasis: 'auto',
  },
  title: {
    alignSelf: 'center',
    marginTop: 16,
    marginBottom: 24,
  },
  rowButton: {
    flex: 1,
  },
  buttonContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  dismissButtonContainer: {
    alignSelf: 'flex-end',
  },
  buttonSpacer: {
    width: 8,
    height: 12,
  },
  tooltipContainer: {
    backgroundColor: 'theme.tooltip.background',
  },
  tooltipText: {
    color: 'theme.tooltip.textPrimary',
  },
})
